import { Box, Typography } from '@mui/material';

interface AIValidationProgressIndicatorProps {
  sicCodeCertainty?: number;
  hasBeenValidated?: boolean;
  size?: 'small' | 'medium' | 'large';
}

const AIValidationProgressIndicator = ({
  sicCodeCertainty = 0,
  hasBeenValidated = false,
  size = 'medium',
}: AIValidationProgressIndicatorProps) => {
  // Calculate progress based on available validation data
  // This is a placeholder implementation that can be enhanced later
  const calculateProgress = () => {
    let progress = 0;

    // Add points for SIC code certainty (0-60 points)
    if (sicCodeCertainty) {
      progress += sicCodeCertainty * 60;
    }

    // Add points for manual validation (40 points)
    if (hasBeenValidated) {
      progress += 40;
    }

    return Math.min(progress * 100, 100); // Convert to percentage and cap at 100
  };

  const progress = calculateProgress();
  const percentage = Math.round(progress);

  // Color logic similar to CrawlerInbox
  let color = 'var(--border-subtle)'; // gray for 0%
  let bgColor = 'var(--surface-inset)';

  if (progress >= 100) {
    color = 'var(--semantic-success)'; // green for 100%
    bgColor = 'var(--surface-inset)';
  } else if (progress >= 66) {
    color = 'var(--semantic-warning)'; // orange for ~66%
    bgColor = 'var(--surface-inset)';
  } else if (progress >= 33) {
    color = '#ff5722'; // deep orange for ~33%
    bgColor = 'var(--surface-inset)';
  }

  // Size configurations
  const sizeConfig = {
    small: {
      height: 4,
      width: 60,
      textSize: '0.7rem',
      padding: '1px 4px',
      minWidth: '20px',
    },
    medium: {
      height: 6,
      width: 80,
      textSize: '0.75rem',
      padding: '2px 6px',
      minWidth: '25px',
    },
    large: {
      height: 8,
      width: 100,
      textSize: '0.8rem',
      padding: '4px 8px',
      minWidth: '30px',
    },
  };

  const config = sizeConfig[size];

  return (
    <Box
      display="flex"
      alignItems="center"
      sx={{
        backgroundColor: bgColor,
        borderRadius: '4px',
        padding: config.padding,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box
          sx={{
            width: config.width,
            height: config.height,
            backgroundColor: 'var(--border-subtle)',
            borderRadius: '3px',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: `${percentage}%`,
              height: '100%',
              backgroundColor: color,
              transition: 'width 0.3s ease',
            }}
          />
        </Box>
        <Typography
          variant="caption"
          sx={{
            minWidth: config.minWidth,
            color: color,
            fontWeight: 'bold',
            fontSize: config.textSize,
          }}
        >
          {percentage}%
        </Typography>
      </Box>
    </Box>
  );
};

export default AIValidationProgressIndicator;
